import { apiClient } from '../api/apiClient';

export type MealPlanMode = 'adaptive' | 'fixed';
/** Meal type key from API (e.g. breakfast, lunch, dinner, snack, brunch, early_morning). */
export type MealPlanMealKey = string;

export interface QualityGatesResult {
  passed: boolean;
  failed_gates?: string[] | null;
}

export interface MealPlanNutrients {
  values: Record<string, number | null>;
}

export interface MealPlanIngredient {
  name: string;
  food_group_key?: string | null;
  portion_display: string;
  portion_grams?: number | null;
  /** Dish-level exception for this ingredient, e.g. 'except banana'. Shown in ingredients list. */
  exception?: string | null;
  nutrients?: MealPlanNutrients;
}

export interface MealPlanMealItem {
  meal_key: MealPlanMealKey;
  /** Time of the meal slot, e.g. '07:00' (HH:MM). Returned by Version API when stored. */
  meal_time?: string | null;
  dish_name: string;
  /** Exception for the whole dish, e.g. 'except banana'. Shown in UI when present. */
  exception?: string | null;
  ingredients: MealPlanIngredient[];
  dish_nutrients?: MealPlanNutrients;
}

export interface MealPlanDay {
  date: string; // ISO date
  items: MealPlanMealItem[];
}

export interface MealPlanContext {
  user_id: number;
  mode: MealPlanMode;
  variant?: string | null;
  duration_days: number;
  language: string;
  cuisine_pattern: string;
  meals_per_day?: number;
  snacks_per_day?: number;
  meal_timing_json?: Record<string, any> | null;
  diet_pattern?: string | null;
  dislikes?: string[] | null;
  allergens?: string[] | null;
  constraints?: any;
  nutrition_targets_ref?: any;
  // Overlap controls
  allow_recipe_repeat?: boolean;
  recipe_repeat_policy?: 'smart' | 'unlimited' | null;
  max_repeats_per_dish?: number | null;
  repeat_after_days?: number | null;
}

export interface MealPlanPreviewRequest {
  context: MealPlanContext;
  sample_size?: number;
}

export interface MealPlanPreviewResponse {
  status: 'preview';
  preview_id: string;
  days: MealPlanDay[];
  quality_gates?: QualityGatesResult | null;
}

export interface MealPlanCreateRequest {
  user_id: number;
  mode: MealPlanMode;
  variant?: string | null;
  language?: string | null;
  cuisine_pattern?: string | null;
  meals_per_day?: number | null;
  snacks_per_day?: number | null;
  meal_timing_json?: Record<string, any> | null;
  name?: string | null;
  start_date?: string | null;
  end_date?: string | null;
  goal_id?: number;
}

export interface MealPlanCreateResponse {
  plan_id: string;
  /** Initial draft version created with the plan (for chat/upload). */
  version_id?: string | null;
  status: 'draft';
}

export interface MealPlanGenerateDraftRequest {
  context: MealPlanContext;
  duration_days: number;
  seed_from?: 'existing_meals' | 'ai' | null;
  generation_scope?: 'full_plan';
}

export interface MealPlanGenerationMetadata {
  status?: 'generating' | 'completed' | 'failed' | null;
  current_day?: number | null;
  total_days?: number | null;
  progress?: number | null;
  error?: string | null;
}

export interface MealPlanVersionResponse {
  plan_id: string;
  version_id: string;
  status: string;
  workflow_state?: 'in_progress' | 'saved' | 'approved' | null;
  days: MealPlanDay[];
  grocery_list?: any[] | null;
  /** Plan-wide items to avoid (from upload). Stored in version.diff_summary_json. */
  overall_exceptions?: string[] | null;
  generation_metadata?: MealPlanGenerationMetadata | null;
}

export interface MealPlanVersionSummary {
  version_id: string;
  version: number;
  status: string;
  workflow_state?: 'in_progress' | 'saved' | 'approved' | null;
  generated_by: string;
  created_at?: string | null;
}

export interface MealPlanVersionsListResponse {
  plan_id: string;
  versions: MealPlanVersionSummary[];
}

export interface NutrientComparison {
  actual: number;
  target: number;
  diff: number;
  diff_percent: number;
  status: 'met' | 'over' | 'under';
}

export interface MealPlanTotalsSummary {
  totals: Record<string, number>;
  meal_plan_totals: Record<string, number | null>;
  meal_plan_base_icmr_totals?: Record<string, number | null>;
  meal_plan_doctor_modified_totals?: Record<string, number | null>;
  meal_plan_adjusted_totals?: Record<string, number | null>;
  comparison: Record<string, NutrientComparison>;
  days_count: number;
}

export interface MealPlanApproveResponse {
  plan_id: string;
  approved_version_id: string;
  status: 'approved';
}

export interface MealPlanAcceptResponse {
  plan_id: string;
  active_version_id: string;
  activated_at: string;
}

export interface MealPlanTodayResponse {
  plan_id: string;
  day_instance_id?: string | null;
  date: string;
  items: MealPlanMealItem[];
  quality_gates?: QualityGatesResult | null;
  persisted: boolean;
}

export interface MealPlanSummary {
  plan_id: string;
  user_id: number;
  mode: MealPlanMode;
  variant?: string | null;
  status: string;
  language: string;
  cuisine_pattern: string;
  meals_per_day: number;
  snacks_per_day: number;
  meal_timing_json?: Record<string, any> | null;
  approved_at?: string | null;
  activated_at?: string | null;
  name?: string | null;
  start_date?: string | null;
  end_date?: string | null;
  goal_id?: number | null;
}

export interface MealPlanListResponse {
  plans: MealPlanSummary[];
}

// ========== Doctor Fixed Manual Upload & Chat Types ==========

export interface DocumentUploadResponse {
  document_id: string;
  s3_uri: string;
  session_id?: number | null;
  plan_id?: string | null;
  version_id?: string | null;
  status: string;
  message: string;
}

export interface MealPlanChatMessageRequest {
  content: string;
  session_id?: number | null;
  version_id?: string | null;
}

export interface MealPlanChatMessageResponse {
  message_id: number;
  session_id: number;
  status: string;
  message: string;
}

export interface MealPlanChatStatusResponse {
  plan_id: string;
  version_id?: string | null;
  session_id: number;
  status: 'processing' | 'ready_for_review' | 'waiting_for_modifications' | 'approved' | 'cancelled';
  plan_approved: boolean;
  waiting_for_doctor_response: boolean;
  conversation_log: Array<{
    role: string;
    content: string;
    created_at?: string | null;
  }>;
  plan_preview?: any;
  icmr_comparison?: Record<string, any>;
  workflow_task_status?: 'processing' | 'waiting_for_doctor' | 'paused' | 'completed' | 'error' | 'cancelled';
  current_step?: string | null;
  current_progress?: number | null;
  last_progress_at?: string | null;
  waiting_for_doctor_since?: string | null;
  workflow_run_id?: string | null;
  resume_lock_until?: string | null;
  cancel_requested?: boolean;
  /** When waiting_for_doctor_response is true, the question the doctor should answer */
  current_ambiguity_question?: {
    question_id?: string;
    question?: string;
    options?: string[];
    suggestions?: string[];
  } | null;
  /** User-friendly error message when workflow failed (for banner / recovery UI) */
  error_message?: string | null;
  /** Suggested recovery action when workflow failed (e.g. try upload again) */
  error_recovery_hint?: string | null;
  /** True when workflow is waiting for user to submit table modifications (table-verify flow). */
  waiting_for_table_verify?: boolean;
  /** Plan meal types for table dropdown (from plan settings). */
  meal_types?: string[] | null;
  /** Meal time per meal type for table dropdown (from plan settings). */
  meal_times_by_type?: Record<string, string> | null;
}

export interface MealPlanApproveResponse {
  plan_id: string;
  approved: boolean;
  message: string;
}

export interface MealPlanUploadVerifyRequest {
  canonical_meal_plan: Record<string, any>;
}

export interface MealPlanUploadVerifyResponse {
  message: string;
  session_id: number;
}

export interface MealPlanSaveDraftResponse {
  plan_id: string;
  version_id: string;
  message: string;
}

export interface IcmrComparisonEntry {
  target: number | null;
  actual: number | null;
  difference: number | null;
  percentage: number | null;
}

export interface ICMRComparisonResponse {
  plan_id: string;
  comparison: Record<string, IcmrComparisonEntry>;
  summary: {
    total_nutrients: number;
    met_targets: number;
    met_targets_percentage: number;
  };
}

class MealPlanService {
  async listPlansForPatient(userId: number, goalId?: number): Promise<MealPlanSummary[]> {
    const url = goalId !== undefined
      ? `/api/v1/daily-meals-plan/patients/${userId}/plans?goal_id=${goalId}`
      : `/api/v1/daily-meals-plan/patients/${userId}/plans`;
    const res = await apiClient.get<MealPlanListResponse>(url);
    return res.data?.plans || [];
  }

  async createPlan(req: MealPlanCreateRequest): Promise<MealPlanCreateResponse> {
    const res = await apiClient.post<MealPlanCreateResponse>(`/api/v1/daily-meals-plan/plans`, req);
    return res.data;
  }

  /** Create a new empty draft version for a plan (e.g. when user clicks "Create New Draft" on choice screen). */
  async createDraftVersion(planId: string): Promise<MealPlanVersionResponse> {
    const res = await apiClient.post<MealPlanVersionResponse>(`/api/v1/daily-meals-plan/plans/${planId}/versions`, {});
    return res.data;
  }

  async generateDraft(planId: string, req: MealPlanGenerateDraftRequest): Promise<MealPlanVersionResponse> {
    const res = await apiClient.post<MealPlanVersionResponse>(`/api/v1/daily-meals-plan/plans/${planId}/generate`, req);
    return res.data;
  }

  async updateVersion(planId: string, versionId: string, payload: { days?: MealPlanDay[]; status?: 'draft_upload' }): Promise<MealPlanVersionResponse> {
    const res = await apiClient.patch<MealPlanVersionResponse>(`/api/v1/daily-meals-plan/plans/${planId}/versions/${versionId}`, payload);
    return res.data;
  }

  async approve(planId: string, versionId: string, message_to_patient?: string): Promise<MealPlanApproveResponse> {
    const res = await apiClient.post<MealPlanApproveResponse>(`/api/v1/daily-meals-plan/plans/${planId}/approve`, {
      version_id: versionId,
      message_to_patient: message_to_patient || null,
    });
    return res.data;
  }

  async getApproved(planId: string): Promise<MealPlanVersionResponse> {
    const res = await apiClient.get<MealPlanVersionResponse>(`/api/v1/daily-meals-plan/plans/${planId}/approved`);
    return res.data;
  }

  async accept(planId: string, approvedVersionId: string): Promise<MealPlanAcceptResponse> {
    const res = await apiClient.post<MealPlanAcceptResponse>(`/api/v1/daily-meals-plan/plans/${planId}/accept`, {
      approved_version_id: approvedVersionId,
    });
    return res.data;
  }

  async getToday(planId: string): Promise<MealPlanTodayResponse> {
    const res = await apiClient.get<MealPlanTodayResponse>(`/api/v1/daily-meals-plan/plans/${planId}/today`);
    return res.data;
  }

  async deletePlan(planId: string): Promise<void> {
    await apiClient.delete(`/api/v1/daily-meals-plan/plans/${planId}`);
  }

  async pausePlan(planId: string): Promise<void> {
    await apiClient.post(`/api/v1/daily-meals-plan/plans/${planId}/pause`, {});
  }

  async archivePlan(planId: string): Promise<void> {
    await apiClient.post(`/api/v1/daily-meals-plan/plans/${planId}/archive`, {});
  }

  async updatePlan(planId: string, updates: {
    mode?: MealPlanMode;
    variant?: string;
    cuisine_pattern?: string;
    meals_per_day?: number;
    snacks_per_day?: number;
    meal_timing_json?: Record<string, any>;
    name?: string;
    start_date?: string;
    end_date?: string;
  }): Promise<MealPlanSummary> {
    const res = await apiClient.patch<MealPlanSummary>(`/api/v1/daily-meals-plan/plans/${planId}`, updates);
    return res.data;
  }

  async getCurrentVersion(planId: string): Promise<MealPlanVersionResponse> {
    const res = await apiClient.get<MealPlanVersionResponse>(`/api/v1/daily-meals-plan/plans/${planId}/current-version`);
    return res.data;
  }

  async listVersions(planId: string): Promise<MealPlanVersionSummary[]> {
    const res = await apiClient.get<MealPlanVersionsListResponse>(`/api/v1/daily-meals-plan/plans/${planId}/versions`);
    return res.data?.versions || [];
  }

  async getVersion(planId: string, versionId: string): Promise<MealPlanVersionResponse> {
    const res = await apiClient.get<MealPlanVersionResponse>(`/api/v1/daily-meals-plan/plans/${planId}/versions/${versionId}`);
    return res.data;
  }

  async deleteVersion(planId: string, versionId: string): Promise<void> {
    await apiClient.delete(`/api/v1/daily-meals-plan/plans/${planId}/versions/${versionId}`);
  }

  async getTotalsSummary(planId: string, versionId?: string, goalId?: number): Promise<MealPlanTotalsSummary> {
    const params = new URLSearchParams();
    if (versionId) params.set('version_id', versionId);
    if (goalId) params.set('goal_id', String(goalId));
    const qs = params.toString();
    const url = qs
      ? `/api/v1/daily-meals-plan/plans/${planId}/totals-summary?${qs}`
      : `/api/v1/daily-meals-plan/plans/${planId}/totals-summary`;
    const res = await apiClient.get<MealPlanTotalsSummary>(url);
    return res.data;
  }

  async getGenerationStatus(planId: string, versionId: string): Promise<MealPlanGenerationMetadata> {
    const res = await apiClient.get<MealPlanGenerationMetadata>(
      `/api/v1/daily-meals-plan/plans/${planId}/versions/${versionId}/generation-status`
    );
    return res.data;
  }

  /**
   * Fetch weekly schedule PDF (for View or Download). Returns blob and suggested filename.
   * Backend generates once and stores in S3; subsequent calls serve from S3 until plan/version changes.
   * Pass regenerate=true to force a fresh PDF (e.g. for "Generate and View").
   */
  async getWeeklySchedulePdf(
    planId: string,
    versionId: string,
    disposition: 'inline' | 'attachment',
    regenerate?: boolean
  ): Promise<{ blob: Blob; filename: string }> {
    const params = new URLSearchParams({ disposition });
    if (regenerate) params.set('regenerate', '1');
    const url = `/api/v1/daily-meals-plan/plans/${planId}/versions/${versionId}/export/weekly-schedule?${params.toString()}`;
    const res = await apiClient.get(url, { responseType: 'blob' });
    const blob = res.data as Blob;
    let filename = 'weekly-schedule.pdf';
    const cd = res.headers?.['content-disposition'];
    if (cd && typeof cd === 'string') {
      const match = /filename="?([^";\n]+)"?/i.exec(cd);
      if (match) filename = match[1].trim();
    }
    return { blob, filename };
  }

  // ========== Doctor Fixed Manual Upload & Chat ==========

  async uploadDocument(params: {
    file: File;
    user_id: number;
    plan_name: string;
    start_date: string;
    end_date: string;
    language: string;
    cuisine_pattern: string;
    meals_per_day: number;
    snacks_per_day: number;
    meal_timing_json: Record<string, any>;
    goal_id?: number;
    plan_id: string;
    version_id: string;
  }): Promise<DocumentUploadResponse> {
    const formData = new FormData();
    formData.append('file', params.file);
    formData.append('user_id', String(params.user_id));
    formData.append('plan_name', params.plan_name);
    formData.append('start_date', params.start_date);
    formData.append('end_date', params.end_date);
    formData.append('language', params.language);
    formData.append('cuisine_pattern', params.cuisine_pattern);
    formData.append('meals_per_day', String(params.meals_per_day));
    formData.append('snacks_per_day', String(params.snacks_per_day));
    formData.append('meal_timing_json', JSON.stringify(params.meal_timing_json || {}));
    if (params.goal_id !== undefined) {
      formData.append('goal_id', String(params.goal_id));
    }
    formData.append('plan_id', String(params.plan_id));
    formData.append('version_id', String(params.version_id));

    const res = await apiClient.post<DocumentUploadResponse>(`/api/v1/daily-meals-plan/upload-document`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return res.data;
  }

  async sendChatMessage(
    planId: string,
    payload: MealPlanChatMessageRequest,
    options?: { idempotencyKey?: string }
  ): Promise<MealPlanChatMessageResponse> {
    const res = await apiClient.post<MealPlanChatMessageResponse>(
      `/api/v1/daily-meals-plan/plans/${planId}/chat/message`,
      payload,
      {
        headers: options?.idempotencyKey
          ? { 'X-Idempotency-Key': options.idempotencyKey }
          : undefined,
      }
    );
    return res.data;
  }

  async getChatStatus(
    planId: string,
    sessionId?: number | null,
    versionId?: string | null
  ): Promise<MealPlanChatStatusResponse> {
    const params = new URLSearchParams();
    if (sessionId) {
      params.set('session_id', String(sessionId));
    }
    if (versionId) {
      params.set('version_id', String(versionId));
    }
    const qs = params.toString();
    const url = qs
      ? `/api/v1/daily-meals-plan/plans/${planId}/chat/status?${qs}`
      : `/api/v1/daily-meals-plan/plans/${planId}/chat/status`;
    const res = await apiClient.get<MealPlanChatStatusResponse>(url);
    return res.data;
  }

  /** Submit table modifications (canonical meal plan) and resume workflow from nutrients. */
  async submitUploadVerify(
    planId: string,
    body: MealPlanUploadVerifyRequest,
    sessionId: number
  ): Promise<MealPlanUploadVerifyResponse> {
    const url = `/api/v1/daily-meals-plan/plans/${planId}/upload/verify?session_id=${sessionId}`;
    const res = await apiClient.post<MealPlanUploadVerifyResponse>(url, body);
    return res.data;
  }

  async approveUploadedPlan(planId: string): Promise<MealPlanApproveResponse> {
    const res = await apiClient.post<MealPlanApproveResponse>(
      `/api/v1/daily-meals-plan/plans/${planId}/approve-uploaded`,
      {}
    );
    return res.data;
  }

  async saveUploadedDraft(planId: string, sessionId?: number | null): Promise<MealPlanSaveDraftResponse> {
    const payload: any = {};
    if (sessionId) payload.session_id = sessionId;
    const res = await apiClient.post<MealPlanSaveDraftResponse>(
      `/api/v1/daily-meals-plan/plans/${planId}/save-uploaded-draft`,
      payload
    );
    return res.data;
  }

  async getIcmrComparison(planId: string): Promise<ICMRComparisonResponse> {
    const res = await apiClient.get<ICMRComparisonResponse>(
      `/api/v1/daily-meals-plan/plans/${planId}/icmr-comparison`
    );
    return res.data;
  }

  async cancelUploadWorkflowSession(sessionId: number): Promise<any> {
    try {
      const response = await apiClient.delete(`/api/v1/daily-meals-plan/plans/upload-session/${sessionId}`);
      return response.data;
    } catch (error) {
      console.error('Failed to cancel upload workflow session:', error);
      throw error;
    }
  }

  async restartChatWorkflowFromLastState(planId: string, sessionId?: number | null): Promise<void> {
    const params = new URLSearchParams();
    if (sessionId) {
      params.set('session_id', String(sessionId));
    }
    const qs = params.toString();
    const url = qs
      ? `/api/v1/daily-meals-plan/plans/${planId}/chat/restart-from-last-state?${qs}`
      : `/api/v1/daily-meals-plan/plans/${planId}/chat/restart-from-last-state`;
    await apiClient.post(url, {});
  }

  async cleanupIdleUploadSessions(idleMinutes = 60): Promise<void> {
    await apiClient.post(`/api/v1/daily-meals-plan/plans/upload-sessions/cleanup-idle?idle_minutes=${idleMinutes}`, {});
  }
}

export const mealPlanService = new MealPlanService();
